/* eslint-disable max-len, no-mixed-operators, prefer-arrow-callback, valid-jsdoc */
import { ChevronRight } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { AccordionDetails, AccordionSummary, Box, Divider, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { getViewsAbbreviated } from '@zefr/style-guide/dist/ui/utils/strings/getViewsAbbreviated';
import React, { useMemo } from 'react';
import { RISK_LEVEL_MAP } from '../../../../atoms/contentAlignmentAtom';
import { useDemoForm } from '../../../../context/DemoFormContext';
import AverageDailyViewsGridItem from '../../../ChannelResults/AverageDailyViewsGridItem';
import ConceptsBox from '../../../ChannelResults/ConceptsBox';
import DonutGraphic from '../../../ChannelResults/DonutGraphic';
import FakeLinearProgressBar from '../../../ChannelResults/FakeLinearProgressBar';
import RiskGridItem from '../../../ChannelResults/RiskGridItem';
import SpendGridItem from '../../../ChannelResults/SpendGridItem';
import {
  PaddedFormControl,
  SummaryAccordion,
  SummaryItems,
} from '../../../ChannelResults/components';
import InternalIcon from '../../../InternalIcon';
import MaxLogoIcon from '../../../MaxLogoIcon';
import VideoTable from '../../../VideoTable';
import DownloadListButton from '../DownloadListButton';
import LanguageFilterAutocomplete from '../LanguageFilterAutocomplete';
import PrimarySortPicker from '../PrimarySortPicker';
import { FormStepProps } from '../types';
import ChannelResultsHeader from './ChannelResultsHeader';

// On xs the donut is hidden so Spend + AvgDV + Risk fit side by side
// (4 + 4 + 4 = 12). On md+ each metric still gets a third alongside the donut.
const metricGridSize = {
  xs: 4,
  md: 4,
};
const riskGridSize = metricGridSize;

export const StepInclusionResults: React.FC<FormStepProps> = ({
  step: myStep = 5,
  'data-testid': testId,
}) => {
  const {
    step,
    selectedPlatform,
    inclusionResults,
    riskLevel,
    listType,
    isInclusionResultFetching,
    isInclusionResultLoading,
    isInclusionResultSuccess,
  } = useDemoForm();

  const riskLevelTitle = useMemo(() => {
    return typeof riskLevel !== 'undefined' && riskLevel >= 0
      ? Object.values(RISK_LEVEL_MAP)[riskLevel]
      : 'Unknown';
  }, [riskLevel]);

  const isLoading = useMemo(
    () => isInclusionResultLoading || isInclusionResultFetching,
    [isInclusionResultFetching, isInclusionResultLoading],
  );

  const isSuccess = useMemo(() => isInclusionResultSuccess, [isInclusionResultSuccess]);

  if (step !== myStep || listType !== 'inclusion') {
    return null;
  }

  return (
    <PaddedFormControl fullWidth focused={true} data-testid={testId}>
      <Grid
        container
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          gap: { xs: 2, md: 10 },
          boxSizing: 'border-box',
        }}
      >
        <Grid id="projected-performance-summary" xs={12}>
          <SummaryAccordion defaultExpanded>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon fontSize="large" />}
              aria-controls="panel1-content"
              id="panel1-header"
            >
              <Box
                display={'flex'}
                flexDirection={'row'}
                justifyContent={'center'}
                alignItems={'center'}
                gap={8}
              >
                <Typography
                  sx={(theme) => ({
                    color: theme.palette.secondary.main,
                  })}
                  variant="h6"
                  fontWeight={'bold'}
                  color="inherit"
                >
                  Projected Performance Summary
                </Typography>
                <Box
                  display={'flex'}
                  flexDirection={'row'}
                  justifyContent={'center'}
                  alignItems={'stretch'}
                  gap={3}
                  sx={{
                    '& .MuiSvgIcon-root': {
                      height: 'unset',
                    },
                  }}
                >
                  <Box
                    display={'flex'}
                    flexDirection={'column'}
                    justifyContent={'center'}
                    alignItems={'flex-start'}
                    boxSizing={'border-box'}
                  >
                    <InternalIcon
                      sx={{ fontSize: '3.75rem' }}
                      iconName={selectedPlatform?.logoIcon}
                    />
                  </Box>
                  <Box
                    display={'flex'}
                    flexDirection={'column'}
                    justifyContent={'center'}
                    alignItems={'flex-start'}
                    boxSizing={'border-box'}
                  >
                    <Divider orientation={'vertical'} />
                  </Box>
                  <Box
                    display={'flex'}
                    flexDirection={'column'}
                    justifyContent={'center'}
                    alignItems={'flex-start'}
                    gap={1}
                    boxSizing={'border-box'}
                    p={1}
                  >
                    <Box
                      display={'flex'}
                      flexDirection={'row'}
                      justifyContent={'center'}
                      alignItems={'center'}
                      gap={1}
                    >
                      <InternalIcon
                        sx={{ fontSize: '3.75rem' }}
                        iconName={selectedPlatform?.labelIcon}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <SummaryItems container gap={3} id="project-performance-summary-splitter">
                {/* md+ donut: sits in its own left column. Hidden on xs because
                    we render an inline xs donut inside the metrics row below
                    so it sits next to Spend + AvgDV instead of stretching the
                    whole left column to match the right column's full height. */}
                <DonutGraphic sx={{ display: { xs: 'none', md: 'flex' } }} />
                <SummaryItems
                  xs={12}
                  md={true}
                  rowGap={1}
                  container
                  columnGap={3}
                  id="project-performance-summary-items"
                >
                  <Grid container gap={3} width="100%">
                    <Grid xs={12} container gap={{ xs: 0.5, md: 3 }} sx={{ flexWrap: 'nowrap' }}>
                      <SpendGridItem
                        {...metricGridSize}
                        value={
                          isLoading ? (
                            <FakeLinearProgressBar />
                          ) : isSuccess ? (
                            inclusionResults?.aggregates.spend
                          ) : (
                            <FakeLinearProgressBar
                              linearProgressProps={{
                                variant: 'determinate',
                                value: 100,
                                color: 'error',
                              }}
                            />
                          )
                        }
                      />
                      <AverageDailyViewsGridItem
                        {...metricGridSize}
                        value={
                          isLoading ? (
                            <FakeLinearProgressBar />
                          ) : isSuccess ? (
                            inclusionResults?.aggregates.total_average_daily_views
                          ) : (
                            <FakeLinearProgressBar
                              linearProgressProps={{
                                variant: 'determinate',
                                value: 100,
                                color: 'error',
                              }}
                            />
                          )
                        }
                      />
                      <RiskGridItem
                        {...riskGridSize}
                        label={`Risk Tolerance Level`}
                        value={
                          <Box display={'flex'} justifyContent={'center'} alignItems={'center'}>
                            {riskLevelTitle}
                            <ChevronRight />
                          </Box>
                        }
                      />
                    </Grid>
                    <Grid
                      xs={12}
                      container
                      gap={3}
                      sx={{
                        flexDirection: { xs: 'column', md: 'row' },
                        flexWrap: 'nowrap',
                      }}
                    >
                      <ConceptsBox isExclusion={false} />
                      <ConceptsBox isExclusion={true} />
                    </Grid>
                  </Grid>
                </SummaryItems>
              </SummaryItems>
            </AccordionDetails>
          </SummaryAccordion>
        </Grid>
        <Grid id="channel-inclusion-list" xs={12}>
          <SummaryAccordion expanded color="black" sx={{ backgroundColor: 'white' }}>
            <AccordionSummary
              aria-controls="panel1-content"
              id="panel1-header"
              TouchRippleProps={{ style: { display: 'none' } }}
              sx={{
                cursor: 'default !important',
                '&:hover': { backgroundColor: 'transparent' },
                '&.Mui-focused, &.Mui-focusVisible': { backgroundColor: 'transparent' },
                '& .MuiAccordionSummary-content': { cursor: 'default' },
                '& .MuiTouchRipple-root': { display: 'none' },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: { xs: 'stretch', md: 'flex-start' },
                  justifyContent: 'space-between',
                  gap: { xs: 2, md: 0 },
                  width: '100%',
                }}
              >
                <ChannelResultsHeader>
                  <Typography
                    sx={(theme) => ({
                      color: theme.palette.secondary.main,
                    })}
                    variant="h6"
                    fontWeight={'bold'}
                    color="inherit"
                  >
                    Channel Inclusion List:{' '}
                    <Typography component={'span'} variant="h6" fontWeight={'thin'} color="inherit">
                      {getViewsAbbreviated(
                        inclusionResults?.aggregates.total_number_of_channels,
                        true,
                      )}{' '}
                      Total
                    </Typography>
                  </Typography>
                  <MaxLogoIcon />
                </ChannelResultsHeader>
                <Box
                  sx={{
                    display: 'flex',
                    // Stack the controls vertically below md so the Language
                    // Filter sits on top of Primary Sort instead of trying
                    // to fit beside it at narrow tablet widths (the donut
                    // chart hides at the same breakpoint, freeing room for
                    // a wider stacked layout).
                    flexDirection: { xs: 'column', md: 'row' },
                    justifyContent: { xs: 'flex-start', md: 'flex-end' },
                    alignItems: { xs: 'stretch', sm: 'flex-end', md: 'flex-start' },
                    flexWrap: { xs: 'nowrap', md: 'wrap' },
                    gap: 2,
                  }}
                >
                  <Box
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      flexShrink: 0,
                      width: { xs: '100%', sm: 260 },
                      // Pin the inner Autocomplete to fill this wrapper so
                      // its empty-state minWidth doesn't make it look
                      // narrower than the Primary Sort dropdown when the
                      // controls wrap onto separate lines.
                      '& .MuiAutocomplete-root': {
                        width: '100%',
                        maxWidth: '100%',
                        minWidth: 0,
                      },
                    }}
                  >
                    <LanguageFilterAutocomplete />
                  </Box>
                  <PrimarySortPicker />
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box
                sx={{
                  px: 4,
                  pb: 4,
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Typography component={'span'} variant="body1" fontWeight={'thin'}>
                  Below are a list of unique channels that you can include in your campaign
                  placement
                </Typography>
                <Box sx={{ flexShrink: 0 }}>
                  <DownloadListButton />
                </Box>
              </Box>
              <VideoTable
                rows={inclusionResults?.channel_results}
                totalRows={inclusionResults?.aggregates.total_number_of_channels}
                isLoading={isInclusionResultLoading || isInclusionResultFetching}
              />
            </AccordionDetails>
          </SummaryAccordion>
        </Grid>
      </Grid>
    </PaddedFormControl>
  );
};

export default StepInclusionResults;
